import React, { useState, useEffect } from 'react';
import Dashboard from '../client/dasboard';
import ModalLogin from '../auth/modal-login';
import ModalRegister from '../auth/modal-register';
import UserManagement from '../superadmin/gestion';
import Events from '../admin/events';
import Blog from '../admin/blog';
import Profile from '../admin/perfil';
import { auth, db, storage } from '../firebaseConfig';
import { onAuthStateChanged, signOut, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { showToast, setToastUserRole } from '../components/Toast';
import '../css/auth.css';
import '../css/topbar.css';

function Base() {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(false);

  // Procesar resultado de Google redirect UNA SOLA VEZ al montar
  useEffect(() => {
    let isMounted = true;

    const handleGoogleRedirect = async () => {
      // signInWithPopup is automatically processed by onAuthStateChanged
    };

    handleGoogleRedirect();

    return () => {
      isMounted = false;
    };
  }, []); // Solo ejecutar al montar

  // Listen for authentication changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        setIsRoleLoading(true);
        // Obtener el rol del usuario desde Firestore
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const roleFromDB = userData.role || 'client';
            setUserRole(roleFromDB);
            setToastUserRole(roleFromDB);
          } else {
            setUserRole('client');
            setToastUserRole('client');
            
            // Crear el documento si no existe
            try {
              const userData = {
                email: currentUser.email,
                displayName: currentUser.displayName || 'Usuario',
                photoURL: currentUser.photoURL || null,
                role: 'client',
                createdAt: serverTimestamp(),
              };
              
              await setDoc(userRef, userData);
            } catch (createError) {
              console.error('❌ Error al crear documento:', createError);
            }
          }
        } catch (error) {
          console.error('❌ Error getting user information:', error);
          setUserRole('client');
          setToastUserRole('client');
        } finally {
          setIsRoleLoading(false);
        }
      } else {
        setUserRole(null);
        setToastUserRole(null);
      }
      
      // Mark that authentication loading is complete
      setIsAuthLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserRole(null);
      showToast('Session closed successfully', 'success');
      setShowUserMenu(false);
      closeMobileMenu();
    } catch (error) {
      showToast('Error closing session', 'error', true);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigation = (view) => {
    setCurrentView(view);
    closeMobileMenu();
  };

  const getRoleBadge = () => {
    switch (userRole) {
      case 'superadmin':
        return '👑 Super Admin';
      case 'admin':
        return '⭐ Admin';
      case 'client':
      default:
        return '🎵 Cliente';
    }
  };

  return (
    <div className="base-container">
      {/* Top Navigation Bar */}
      <nav className="topbar">
        <div className="topbar-container">
          {/* Logo/Brand */}
          <div className="topbar-logo">
            <img src="/logo.png" alt="Starboy Records" />
            <span>STARBOY RECORDS</span>
          </div>

          {/* Hamburger Button - Visible on mobile only */}
          <button 
            className={`hamburger-btn ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* Main Menu */}
          <div className={`topbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <button 
              className="topbar-btn"
              onClick={() => handleNavigation('home')}
            >
              <span className="icon">🏠</span> Home
            </button>
            
            <button 
              className="topbar-btn"
              onClick={() => handleNavigation('events')}
            >
              <span className="icon">🎭</span> Events
            </button>
            
            <button 
              className="topbar-btn"
              onClick={() => handleNavigation('blog')}
            >
              <span className="icon">📝</span> Blog
            </button>

            <button 
              className="topbar-btn"
              onClick={() => handleNavigation('merchandising')}
            >
              <span className="icon">🛍️</span> Merchandising
            </button>

            {/* Opciones de Super Admin */}
            {!isRoleLoading && userRole === 'superadmin' && (
              <>
                <div className="topbar-divider"></div>
                
                <button 
                  className="topbar-btn superadmin-btn"
                  onClick={() => handleNavigation('user-management')}
                  title="Solo para superadmins"
                >
                  <span className="icon">⚙️</span> User Management
                </button>
              </>
            )}
          </div>

          {/* User Section */}
          <div className="topbar-user-section">
            {user ? (
              <div className="user-profile-dropdown">
                <button 
                  className="user-profile-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <div className="user-avatar-small">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'Usuario'}
                        onError={async (e) => {
                          // Si la URL falla (token expirado), intentar regenerar desde la ruta
                          console.warn('Avatar URL failed, attempting to regenerate...');
                          try {
                            const userRef = doc(db, 'users', user.uid);
                            const userDoc = await getDoc(userRef);
                            if (userDoc.exists() && userDoc.data().photoPath) {
                              const newURL = await getDownloadURL(ref(storage, userDoc.data().photoPath));
                              e.target.src = newURL;
                              await updateProfile(user, { photoURL: newURL });
                            }
                          } catch (err) {
                            console.error('Could not regenerate avatar URL:', err);
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    ) : (
                      <div className="avatar-placeholder-small">
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="user-name-small">{user.displayName || 'Usuario'}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
                
                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    <div className="user-info-header">
                      <p className="user-role-badge">{userRole === 'superadmin' ? '👑 Super Admin' : userRole === 'admin' ? '⭐ Admin' : '🎵 Cliente'}</p>
                      <p className="user-email-small">{user.email}</p>
                    </div>
                    <button 
                      className="topbar-btn profile-btn"
                      onClick={() => {
                        handleNavigation('profile');
                        setShowUserMenu(false);
                      }}
                    >
                      <span className="icon">👤</span> Mi Perfil
                    </button>
                    <button 
                      className="topbar-btn logout-btn"
                      onClick={handleLogout}
                    >
                      <span className="icon">🚺</span> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <button 
                  className="topbar-btn auth-btn login-btn"
                  onClick={() => {
                    setShowLogin(true);
                    closeMobileMenu();
                  }}
                >
                  <span className="icon">🔐</span> Login
                </button>
                
                <button 
                  className="topbar-btn auth-btn register-btn"
                  onClick={() => {
                    setShowRegister(true);
                    closeMobileMenu();
                  }}
                >
                  <span className="icon">✍️</span> Register
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Overlay to close mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="menu-overlay" 
          onClick={closeMobileMenu}
        ></div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {isAuthLoading ? (
          <div className="loading-screen">
            <h1>Loading...</h1>
          </div>
        ) : (
          <>
            {currentView === 'home' && <Dashboard />}
            {currentView === 'events' && <Events userRole={userRole} />}
            {currentView === 'blog' && <Blog />}
            {currentView === 'admin-panel' && (userRole === 'admin' || userRole === 'superadmin') && (
              <div className="coming-soon">
                <h1>🛠️ Administration Panel</h1>
                <p>Coming Soon...</p>
              </div>
            )}
            {currentView === 'user-management' && userRole === 'superadmin' && (
              <UserManagement />
            )}
            {currentView === 'profile' && user && <Profile />}
            {currentView === 'merchandising' && (
              <div className="coming-soon">
                <h1>🛍️ Merchandising</h1>
                <p>Coming Soon...</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modales */}
      {showLogin && <ModalLogin onClose={() => setShowLogin(false)} />}
      {showRegister && <ModalRegister onClose={() => setShowRegister(false)} />}
    </div>
  );
}

export default Base;
